import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { Campo, Cartao, CorpoCartao } from '@/componentes/ui/primitivos';

interface ConfigEtiquetas {
  ativa: boolean;
  /** Crachás do piloto; vazio = todos. */
  crachas: string[];
}

/**
 * Funções novas que se liga e desliga sem deploy (`WMS.CONFIG`).
 *
 * Existe para testar na operação com uma saída rápida: deu problema, desliga
 * aqui e o checkout volta ao que era, em segundos.
 */
export function Funcoes(): JSX.Element {
  const cliente = useQueryClient();
  const chave = ['admin', 'config', 'etiquetas-expedicao'];
  const consulta = useQuery<ConfigEtiquetas>({
    queryKey: chave,
    queryFn: () => api.get('/api/admin/config/etiquetas-expedicao'),
  });
  const mudar = useMutation({
    mutationFn: (corpo: Partial<ConfigEtiquetas>) =>
      api.put<ConfigEtiquetas>('/api/admin/config/etiquetas-expedicao', corpo),
    onSuccess: (d) => cliente.setQueryData(chave, d),
  });

  const ativa = consulta.data?.ativa ?? false;
  const crachasSalvos = (consulta.data?.crachas ?? []).join(', ');
  const [crachas, definirCrachas] = React.useState('');
  React.useEffect(() => definirCrachas(crachasSalvos), [crachasSalvos]);
  const lista = crachas.split(/[\s,;]+/).filter((c) => /^\d+$/.test(c));
  const mudouLista = lista.join(', ') !== crachasSalvos;

  return (
    <Cartao>
      <CorpoCartao className="flex flex-wrap items-start gap-4">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primaria-suave text-primaria">
          <Printer className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[15px] font-bold">Impressão de etiquetas de expedição pelo WMS</h2>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[11px] font-bold',
                ativa ? 'bg-status-livreFundo text-status-livre' : 'bg-superficie2 text-textoFraco',
              )}
            >
              {consulta.isLoading ? '…' : ativa ? 'LIGADA' : 'DESLIGADA'}
            </span>
          </div>
          <form
            className="flex flex-wrap items-center gap-2 pt-1"
            onSubmit={(e) => {
              e.preventDefault();
              mudar.mutate({ crachas: lista });
            }}
          >
            <span className="text-[12.5px] font-semibold">Só estes crachás:</span>
            <Campo
              value={crachas}
              onChange={(e) => definirCrachas(e.target.value.replace(/[^\d,; ]/g, ''))}
              placeholder="vazio = todos (ex.: 85, 90)"
              aria-label="Crachás do piloto"
              className="tabular w-[220px] font-mono"
            />
            <Botao type="submit" tamanho="pequeno" disabled={!mudouLista} carregando={mudar.isPending}>
              Salvar crachás
            </Botao>
            <span className="text-[11.5px] text-textoFraco">
              {crachasSalvos ? `Piloto: ${crachasSalvos} — os outros seguem no .exe.` : 'Todos os crachás.'}
            </span>
          </form>
          <p className="text-[12.5px] leading-relaxed text-textoFraco">
            Ligada, o WMS imprime o DANFE simplificado na Zebra assim que o pedido é faturado, no
            computador onde o crachá estiver logado — o lugar do ERPM_ETIQUETAS.exe — e cada login
            com crachá fica com uma sessão só. Desligada, o WMS não pega etiqueta nenhuma e o .exe
            volta a imprimir; todos os checkouts param em até 20 segundos.
          </p>
        </div>
        <Botao
          variante={ativa ? 'perigo' : 'primaria'}
          carregando={mudar.isPending}
          disabled={consulta.isLoading}
          onClick={() => mudar.mutate({ ativa: !ativa })}
        >
          {ativa ? 'Desligar' : 'Ligar'}
        </Botao>
      </CorpoCartao>
    </Cartao>
  );
}
